
import { useState, useCallback } from 'react';
import { ChatMessage } from '../types';
import { useToast } from '@/hooks/use-toast';

export const useChatMessages = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m your HR assistant powered by Claude. How can I help you today with any human resources questions or concerns?',
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;
    
    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Format messages for Anthropic API
      const messageHistory = messages.concat(userMessage).map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Call Supabase Edge Function to get Claude's response
      const response = await fetch('/api/claude-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          messages: messageHistory,
          systemPrompt: "You are Claude, an AI assistant specialized in HR knowledge. You provide accurate, helpful information about HR best practices, employment laws, workplace regulations, team building, conflict resolution, and other HR-related topics. Your responses should be professional, considerate, and reflect current HR standards. Keep answers concise yet informative, focusing on practical advice when appropriate. If asked about legal topics, clarify that you're not providing legal advice and recommend consulting with an HR professional or employment lawyer for specific situations."
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to get response from Claude');
      }
      
      const data = await response.json();
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to get a response from Claude. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [messages, toast]);
  
  return {
    messages,
    isLoading,
    sendMessage,
  };
};
